import { Currency } from '@mobi/utils/money'
import type { KambiBetOffer } from '@mobi/api-types/kambi'
import type { KambiValidReward } from '@mobi/api-types/kambi/player/reward'
import type {
  KambiSportsSameGameMultiSelection,
  KambiSportsSelection,
  KambiSportsSingleSelection,
  Selection,
} from '@mobi/betslip/types'
import { formatKambiCurrencyToDollarDecimal } from '@mobi/sports-offering/helpers/currency/currencyFormat'
import {
  isKambiSportsMultiSelection,
  isKambiSportsSameGameMultiSelection,
  isKambiSportsSingleSelection,
} from '@mobi/betslip/helpers/typeGuards'

export function getPriceFromKambiSportsSelection(selection: KambiSportsSelection): number {
  if (selection.betSelectionType === 'single') {
    return formatKambiCurrencyToDollarDecimal(getOddsForSelection(selection))
  }

  if (selection.betSelectionType === 'same-game-multi') {
    return formatKambiCurrencyToDollarDecimal(selection.odds)
  }

  if (selection.betSelectionType === 'multi') {
    const legOdds = selection.legs.map(getOddsForSelection)
    const legsMultipliedTotal = legOdds.reduce((acc, curr) => acc.multiply(curr), new Currency(1))
    const oddsAsThousandMultiplier = legsMultipliedTotal.divide(1_000 ** --legOdds.length).value
    return formatKambiCurrencyToDollarDecimal(oddsAsThousandMultiplier)
  }

  return 0
}

export function getSelectionNameFromKambiBetOffer(betOffer: KambiBetOffer): string {
  const outcome = betOffer.outcomes[0]
  return `${outcome.participant || outcome.label}${outcome.line ? ' ' + outcome.line / 1000 : ''}`
}

export function isKambiCashOutAvailable(selection: Selection | null) {
  return (
    (isKambiSportsSingleSelection(selection) && selection.betOffer.cashOutStatus === 'ENABLED') ||
    (isKambiSportsMultiSelection(selection) &&
      selection.legs.every(x =>
        isKambiSportsSingleSelection(x) ? x.betOffer.cashOutStatus === 'ENABLED' : false
      ))
  )
}

export function isKambiEarlySettlementAvailable(selection: Selection | null) {
  if (isKambiSportsSingleSelection(selection)) {
    return selection.betOffer.outcomes.every(
      x =>
        x.tags?.includes('ELIGIBLE_FOR_EARLY_SETTLEMENT') ||
        x.outcomeTags?.includes('ELIGIBLE_FOR_EARLY_SETTLEMENT')
    )
  }

  if (isKambiSportsSameGameMultiSelection(selection)) {
    return false
  }

  if (isKambiSportsMultiSelection(selection)) {
    return selection.legs.every(x => {
      if (isKambiSportsSameGameMultiSelection(x)) {
        return false
      } else if (isKambiSportsSingleSelection(x)) {
        return x.betOffer.outcomes.every(
          x =>
            x.tags?.includes('ELIGIBLE_FOR_EARLY_SETTLEMENT') ||
            x.outcomeTags?.includes('ELIGIBLE_FOR_EARLY_SETTLEMENT')
        )
      } else {
        return false
      }
    })
  }
  return false
}

export function calculateKambiSelectionBoostedPriceWithReward(
  price: number,
  reward: KambiValidReward
): number {
  if (reward.rewardType === 'ODDS_BOOST' && reward.oddsBoostProperties) {
    return formatKambiCurrencyToDollarDecimal(reward.oddsBoostProperties.boostedOdds)
  }

  if (reward.rewardType === 'PROFIT_BOOST' && reward.profitBoostProperties) {
    return applyPercentageBoost(price, reward.profitBoostProperties.boostPercentage)
  }

  return price
}

// =============
// Local Helpers
// =============

function applyPercentageBoost(price: number, boost: number): number {
  return price * (1 + boost / 100) - boost / 100
}

function getOddsForSelection(
  selection: KambiSportsSingleSelection | KambiSportsSameGameMultiSelection
): number {
  return isKambiSportsSingleSelection(selection)
    ? selection.betOffer.outcomes[0].odds
    : selection.odds
}
